import { useEffect, useState } from "react";
import { ChevronDown, ChevronLeft, ChevronRight, ClipboardList } from "lucide-react";
import { getWalkinSubmissions } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { todayLocal } from "@/lib/periods";

// New Consult: every pre-sale questionnaire at this gym for a day, week or month, and whether each person
// had a consult (the consult saved from the form, else one with the same phone). Closed until opened.
const PERIODS = [["day", "Day"], ["week", "Week"], ["month", "Month"]];
const TONE = {
  sold: "border-emerald-500/40 bg-emerald-500/10 text-emerald-200",
  consulted: "border-blue-500/40 bg-blue-500/10 text-blue-200",
  waiting: "border-amber-500/40 bg-amber-500/10 text-amber-200",
  no_consult: "border-rose-500/40 bg-rose-500/10 text-rose-200",
};

const shift = (isoDate, days) => {
  const d = new Date(`${isoDate}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
};

export default function QuestionnaireSubmissions() {
  const { activeFacility } = useAuth();
  const tz = activeFacility?.timezone || undefined;
  const [open, setOpen] = useState(false);
  const [period, setPeriod] = useState("week");
  const [day, setDay] = useState(null);             // any day in the period shown; null = today
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setError("");
    getWalkinSubmissions(period, day).then(setData).catch((e) => setError(e?.response?.data?.detail || "Couldn't load the questionnaires."));
  }, [open, period, day, activeFacility?.id]);

  const today = todayLocal(tz);
  const fmtDay = (iso, withYear) => new Date(`${iso}T12:00:00Z`).toLocaleDateString(undefined,
    { day: "numeric", month: "short", ...(withYear ? { year: "numeric" } : {}), timeZone: "UTC" });
  const label = !data ? "" : data.period === "day" ? fmtDay(data.from, true)
    : data.period === "month" ? new Date(`${data.from}T12:00:00Z`).toLocaleDateString(undefined, { month: "long", year: "numeric", timeZone: "UTC" })
    : `${fmtDay(data.from)} to ${fmtDay(data.to, true)}`;
  const isCurrent = data && data.from <= today && today <= data.to;
  const t = data?.totals;

  return (
    <section className="card-surface p-4" data-testid="questionnaire-submissions">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <button type="button" onClick={() => setOpen((v) => !v)} aria-expanded={open} className="flex items-center gap-2 text-left">
          <ChevronDown className={`h-4 w-4 text-zinc-500 transition-transform ${open ? "" : "-rotate-90"}`} />
          <ClipboardList className="h-4 w-4 text-zinc-400" />
          <span className="eyebrow">Pre-sale questionnaires</span>
        </button>
        {!open && <span className="text-sm text-zinc-400">Everyone who filled it in, and who had a consult</span>}
        {open && t && (
          <span className="text-sm text-zinc-400">
            <b className="text-zinc-100 tabular-nums">{t.submitted}</b> filled in · <b className="text-blue-200 tabular-nums">{t.consulted}</b> had a consult
            {" "}· <b className="text-emerald-300 tabular-nums">{t.sold}</b> sold
            {t.waiting > 0 && <> · <b className="text-amber-200 tabular-nums">{t.waiting}</b> still waiting</>}
          </span>
        )}
      </div>

      {open && (
        <div className="mt-3 space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex rounded-full border border-[#262626] p-0.5">
              {PERIODS.map(([k, name]) => (
                <button key={k} type="button" onClick={() => { setPeriod(k); setDay(null); }} data-testid={`questionnaire-period-${k}`}
                  className={`rounded-full px-3 py-1 text-xs ${period === k ? "bg-rose-600 text-white" : "text-zinc-300 hover:text-white"}`}>
                  {name}
                </button>
              ))}
            </div>
            <button type="button" aria-label="Previous" disabled={!data} onClick={() => setDay(shift(data.from, -1))}
              className="rounded-full border border-[#262626] p-1.5 text-zinc-300 hover:border-rose-500/50 disabled:opacity-40">
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="min-w-[9rem] text-center text-sm text-zinc-200" data-testid="questionnaire-period-label">{label}</span>
            <button type="button" aria-label="Next" disabled={!data || data.to >= today} onClick={() => setDay(shift(data.to, 1))}
              className="rounded-full border border-[#262626] p-1.5 text-zinc-300 hover:border-rose-500/50 disabled:opacity-40">
              <ChevronRight className="h-4 w-4" />
            </button>
            {data && !isCurrent && (
              <button type="button" onClick={() => setDay(null)} className="text-xs text-zinc-400 underline hover:text-zinc-200">
                Back to {period === "day" ? "today" : `this ${period}`}
              </button>
            )}
          </div>

          {error && <p className="text-sm text-rose-300">{error}</p>}
          {data && !data.rows.length && <p className="text-sm text-zinc-500">No pre-sale questionnaires {period === "day" ? "on this day" : `in this ${period}`}.</p>}
          {data && data.rows.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-zinc-500 border-b border-[#1f1f1f]">
                    <th className="py-2 pr-3 font-medium">Name</th>
                    <th className="py-2 px-3 font-medium">Phone</th>
                    <th className="py-2 px-3 font-medium">Filled in</th>
                    <th className="py-2 pl-3 font-medium">Consult</th>
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map((r) => (
                    <tr key={r.id} className="border-b border-[#1a1a1a] last:border-0 align-top">
                      <td className="py-2 pr-3 text-zinc-100">{r.name || "—"}</td>
                      <td className="py-2 px-3 text-zinc-400 tabular-nums whitespace-nowrap">{r.phone || "—"}</td>
                      <td className="py-2 px-3 text-zinc-400 whitespace-nowrap">
                        {new Date(r.created_at).toLocaleString(undefined, { day: "numeric", month: "short", hour: "numeric", minute: "2-digit", timeZone: tz })}
                      </td>
                      <td className="py-2 pl-3">
                        <span className={`inline-block rounded-full border px-2 py-0.5 text-xs whitespace-nowrap ${TONE[r.status] || TONE.no_consult}`}>{r.status_label}</span>
                        {r.lead && (
                          <div className="mt-1 text-xs text-zinc-500">
                            {r.lead.sales_ex ? `${r.lead.sales_ex} · ` : ""}{r.lead.date ? fmtDay(String(r.lead.date).slice(0, 10)) : ""}
                            {r.matched_by === "phone" && <span title="This consult wasn't started from the questionnaire. It was matched by phone number."> · matched by phone</span>}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
